import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';

function authLog(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.debug('[auth]', ...args);
  }
}

export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

export interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  /** True when Supabase env vars are configured. */
  configured: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Authentication shell (Slice 0 foundation).
 *
 * The Partner Onboarding form itself is public-facing. This shell establishes
 * the session-tracking foundation required for the future internal review
 * capability (Slice 6), where authenticated sessions will be required.
 *
 * When Supabase is not configured (e.g. local dev without env vars), the shell
 * degrades gracefully to an unauthenticated state without throwing.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const [configured] = useState<boolean>(() => isSupabaseConfigured());

  useEffect(() => {
    if (!configured) {
      authLog('Supabase not configured; auth shell disabled');
      setStatus('unauthenticated');
      return;
    }

    const supabase = getSupabase();

    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setStatus(data.session ? 'authenticated' : 'unauthenticated');
      })
      .catch((err) => {
        authLog('getSession error', err);
        if (!active) return;
        setSession(null);
        setStatus('unauthenticated');
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return;
      setSession(nextSession);
      setStatus(nextSession ? 'authenticated' : 'unauthenticated');
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [configured]);

  const signOut = useCallback(async () => {
    if (!configured) return;
    try {
      await getSupabase().auth.signOut();
    } catch (err) {
      authLog('signOut error', err);
    }
    setSession(null);
    setStatus('unauthenticated');
  }, [configured]);

  return (
    <AuthContext.Provider value={{ status, session, configured, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
